import "./CoursePage.css";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Header from "./components/Header";
import CourseCard from "./components/CourseCard";
import Footer from "./components/Footer";
import { useDispatch, useSelector } from "react-redux";
import AddEditCourse from "../Edit2/AddCourseForm/AddEditCourse";
import { getCourse, getPrerequisites } from "../../actions/courses";
import AuthModal from "./components/AuthModal";
import { useMathJax } from "../../hooks/useMathJax";
import { processText } from "../../utils/learnUtils";

export default function CoursePage() {
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem("profile"));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [authMode, setAuthMode] = useState(null); // null | 'signin' | 'signup'
  const openAuth = (mode) => {
    setAuthMode(mode);
  };
  const closeAuth = () => setAuthMode(null);

  const [editingID, setEditingID] = useState(-1);
  const [courseLoading, setCourseLoading] = useState(true);
  const course = useSelector((state) => state.CoursesReducer.course);
  const prerequisites = useSelector(
    (state) => state.CoursesReducer.prerequisites,
  );

  // courseLoading is included so the effect re-fires on the render where the
  // ref actually mounts (the loading/not-found early returns below mean the
  // ref is still null on earlier renders where courseDetailsLong changes).
  const longDescRef = useRef(null);
  useMathJax(longDescRef, [course?.courseDetailsLong, courseLoading]);

  // Fetch the course details
  useEffect(() => {
    setCourseLoading(true);
    dispatch(getCourse(id)).then(() => setCourseLoading(false));
  }, [id, dispatch]);

  // Find out if user has taken this course
  var alreadyTakingCourse = false;
  if (course) {
    user?.result.coursesTaken.forEach((courseTaken) => {
      if (course._id === courseTaken.courseID) alreadyTakingCourse = true;
    });
  }

  // Fetch the prerequisite details
  useEffect(() => {
    if (course) {
      // Retrieve prerequisites
      dispatch(
        getPrerequisites({ searchTags: course?.prerequisites.join(";") }),
      );
    }
  }, [course, dispatch]);

  // LearnPage adds the course to the user record on first visit
  const startCourse = () => {
    navigate(`/learn/${course._id}`);
  };

  const editCourse = () => {
    navigate(`/editcoursestructure/${course._id}`);
  };

  const navigateToAuth = () => {
    navigate("/auth");
  };

  const editCourseDetails = () => {
    setEditingID(course._id);
  };

  const updateCourseDetails = () => {
    setEditingID(-1);
    dispatch(getCourse(id));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (courseLoading) {
    return (
      <>
        <Header />
        <main className="course-page course-page--not-found">
          <div className="container">
            <div className="course-page__loading">
              <span className="course-page__loading-text">Loading</span>
              <span className="course-page__loading-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!course) {
    return (
      <>
        <Header />
        <main className="course-page course-page--not-found">
          <div className="container">
            <h1 className="display-lg">Course not found</h1>
            <p>The course you're looking for doesn't exist.</p>
            <Link to="/" className="back-btn">
              ← Back to courses
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryColor = getCategoryColor(course.title);

  const videoSrc = course.videoURL;

  // A single newline the author typed to wrap a sentence should collapse to a
  // space (as plain HTML text would); only a blank line starts a new paragraph.
  const longDescParagraphs = (course.courseDetailsLong || "")
    .replace(/(?<!\n)\n(?!\n)/g, " ")
    .split(/\n{2,}/)
    .filter((para) => para.trim());

  const arrowRight = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );

  return (
    <>
      <Header />
      <main className="course-page">
        {/* ── Hero banner ── */}
        <div
          className="course-page__hero"
          style={{ "--cat-color": categoryColor }}
        >
          <div className="course-page__hero-bg" />
          <div className="container">
            <nav className="course-page__breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="course-page__breadcrumb-link">
                Courses
              </Link>
              <span aria-hidden="true"> / </span>
              <span>{course.title}</span>
            </nav>

            <div className="course-page__hero-inner">
              <div className="course-page__hero-text">
                <span
                  className="course-page__category"
                  style={{ color: categoryColor }}
                >
                  {getCategoryLabel(course.title)}
                </span>
                <h1 className="display-lg course-page__title">
                  {course.title}
                </h1>
                <p className="course-page__subtitle">{course.subtitle}</p>

                <div className="course-page__meta">
                  <span className="course-page__meta-item">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {course.duration}
                  </span>
                  <span className="course-page__meta-item">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                    {course.courseContentList.length} sections
                  </span>
                </div>

                <div className="course-details-buttons">
                  {user?.result?.admin && (
                    <>
                      <button
                        onClick={editCourse}
                        className="course-page__start-btn"
                        style={{ backgroundColor: "purple" }}
                      >
                        Edit content {arrowRight}
                      </button>
                      <button
                        onClick={editCourseDetails}
                        className="course-page__start-btn"
                        style={{ backgroundColor: "purple" }}
                      >
                        Edit details {arrowRight}
                      </button>
                    </>
                  )}
                  {!user ? (
                    <button
                      onClick={() => {
                        openAuth("signup");
                      }}
                      className="course-page__start-btn"
                      style={{ backgroundColor: categoryColor }}
                    >
                      Sign in to start {arrowRight}
                    </button>
                  ) : alreadyTakingCourse ? (
                    <button
                      onClick={startCourse}
                      className="course-page__start-btn"
                      style={{ backgroundColor: categoryColor }}
                    >
                      Continue {arrowRight}
                    </button>
                  ) : (
                    <button
                      onClick={startCourse}
                      className="course-page__start-btn"
                      style={{ backgroundColor: categoryColor }}
                    >
                      Start {arrowRight}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="course-page__body">
          <div className="container">
            <div className="course-page__layout">
              {/* ── Main column ── */}
              <div className="course-page__main">
                {/* Video */}
                {course.videoURL && (
                  <section className="course-page__section">
                    <h2 className="course-page__section-title">
                      Introduction Video
                    </h2>
                    <div className="course-page__video-wrap">
                      <iframe
                        src={videoSrc}
                        title={`${course.title} introduction video`}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                        allowFullScreen
                      />
                    </div>
                  </section>
                )}

                {/* Long description */}
                <section className="course-page__section">
                  <h2 className="course-page__section-title">
                    About this course
                  </h2>
                  <div className="course-page__long-desc" ref={longDescRef}>
                    {longDescParagraphs.map((para, i) => (
                      <p
                        key={i}
                        dangerouslySetInnerHTML={{
                          __html: processText(para),
                        }}
                      />
                    ))}
                  </div>
                </section>

                {/* Content list */}
                <section className="course-page__section">
                  <h2 className="course-page__section-title">
                    What you'll cover
                  </h2>
                  <ol className="course-page__content-list">
                    {course.courseContentList.map((item, i) => (
                      <li key={i} className="course-page__content-item">
                        <span
                          className="course-page__content-num"
                          style={{ color: categoryColor }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="course-page__content-info">
                          <h3 className="course-page__content-title">
                            {item.title}
                          </h3>
                          {item?.youwilllearn && (
                            <p className="course-page__content-summary">
                              {item.youwilllearn}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>
              </div>

              {/* ── Sidebar ── */}
              <aside className="course-page__sidebar">
                {/* Tags */}
                <div className="course-page__sidebar-card">
                  <h3 className="course-page__sidebar-title">Topics covered</h3>
                  <div className="course-page__tags">
                    {course.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/?search=${encodeURIComponent(tag)}`}
                        className="course-page__tag"
                        style={{ "--tag-color": categoryColor }}
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Prerequisites */}
                {prerequisites?.data?.length > -1 && (
                  <div className="course-page__sidebar-card">
                    <h3 className="course-page__sidebar-title">
                      Prerequisites
                    </h3>
                    <div className="course-page__prereqs">
                      {prerequisites?.data?.map((pc) => (
                        <CourseCard
                          key={pc.id}
                          course={pc}
                          type={"new"}
                          compact
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Back link */}
                <Link to="/" className="course-page__back-btn">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    aria-hidden="true"
                  >
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  All courses
                </Link>
              </aside>
            </div>
          </div>
        </div>
      </main>
      {user?.result?.admin && editingID !== -1 && (
        <AddEditCourse currentId={editingID} onClose={updateCourseDetails} />
      )}
      <Footer />
      {authMode && <AuthModal initialMode={authMode} onClose={closeAuth} />}
    </>
  );
}

function getCategoryLabel(title) {
  if (title.startsWith("Machine Learning")) return "Machine Learning";
  if (title.startsWith("Probability")) return "Probability";
  if (title.startsWith("SAT")) return "SAT Solvers";
  return "Course";
}

function getCategoryColor(title) {
  if (title.startsWith("Machine Learning")) return "#4469d8";
  if (title.startsWith("Probability")) return "#2a7a5a";
  if (title.startsWith("SAT")) return "#c05e20";
  return "#6b5ea8";
}
